import { useMemo, useState, useEffect, useRef } from "react";

import type { Course, Screen } from "./types";

// Pure helpers (exported for unit testing) reproduce the source HTML's player
// JS exactly (CPR_Course_Module1.html render()/fmtTime()/fillScore()).

export function progressPct(idx: number, total: number): number {
    return ((idx + 1) / total) * 100;
}

export function timeLeftLabel(idx: number, times: number[]): string {
    let secs = 0;
    for (let i = idx; i < times.length; i++) {
        secs += times[i] ?? 30;
    }
    const mins = Math.max(1, Math.ceil(secs / 60));
    return `~${mins} min left`;
}

/** The pill in the player chrome — the course's own name, not a module index. */
export function coursePill(idx: number, total: number, courseLabel: string): string {
    if (idx >= total - 1) {
        return `${courseLabel} ✓`;
    }
    return courseLabel;
}

export function scoreColor(pct: number): string {
    if (pct >= 75) {
        return "#12A150";
    }
    if (pct >= 50) {
        return "#E8911A";
    }
    return "#E5484D";
}

// Quiz is "try again until correct": wrong picks are recorded and disabled, the
// correct answer is never revealed on a wrong attempt, and the screen only
// unlocks once `solved`. `triedWrong` empty at solve time === first-try correct.
type QuizState = { selected: number | null; triedWrong: number[]; solved: boolean };
/** How long the green "Correct." stays up before the next question slides in. */
const CORRECT_ANSWER_DWELL_MS = 1100;

const EMPTY_QUIZ: QuizState = { selected: null, triedWrong: [], solved: false };

export type CoursePlayer = {
    idx: number;
    screen: Screen;
    total: number;
    progressPct: number;
    timeLeftLabel: string;
    stepLabel: string;
    coursePill: string;
    /** 1-based position of the current quiz among the course's quizzes. */
    quizIndex: number;
    quizCount: number;
    canBack: boolean;
    isLastScreen: boolean;
    quiz: QuizState;
    score: { correct: number; total: number; pct: number; color: string };
    selectOption: (i: number) => void;
    checkAnswer: () => void;
    advance: () => void;
    back: () => void;
};

/**
 * @param course   the course to play, as served by the API
 * @param initialIdx  resume position, restored from the device
 */
export function useCoursePlayer(course: Course, initialIdx = 0): CoursePlayer {
    const screens = course.screens;
    const total = screens.length;
    const times = useMemo(() => screens.map((s) => s.timeSec), [screens]);
    // Screen index -> 1-based quiz number, so a quiz screen never has to
    // carry its own position in the course.
    const quizPositions = useMemo(() => {
        const m = new Map<number, number>();
        let n = 0;
        screens.forEach((s, i) => {
            if (s.kind === "quiz") {
                n += 1;
                m.set(i, n);
            }
        });
        return m;
    }, [screens]);
    const [idx, setIdx] = useState(Math.min(Math.max(initialIdx, 0), Math.max(total - 1, 0)));
    const [quizByIdx, setQuizByIdx] = useState<Record<number, QuizState>>({});

    //  Pending auto-advance after a correct answer. A ref, not state: it must
    //  survive re-renders without causing one, and must be cancellable from
    //  advance()/back() and on unmount so a timer never fires into a screen
    //  the nurse has already left.
    const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    function clearAutoAdvance() {
        if (autoAdvanceRef.current) {
            clearTimeout(autoAdvanceRef.current);
            autoAdvanceRef.current = null;
        }
    }
    useEffect(() => clearAutoAdvance, []);

    const screen = screens[idx];
    const quiz = quizByIdx[idx] ?? EMPTY_QUIZ;

    const score = useMemo(() => {
        // Correct === solved on the first try (no wrong attempts recorded).
        const quizScreens = screens.map((s, i) => ({ s, i })).filter((x) => x.s.kind === "quiz");
        let correct = 0;
        for (const { i } of quizScreens) {
            const st = quizByIdx[i];
            if (st?.solved && st.triedWrong.length === 0) {
                correct++;
            }
        }
        const denom = quizScreens.length || 1;
        const pct = Math.round((correct / denom) * 100);
        return { correct, total: quizScreens.length, pct, color: scoreColor(pct) };
    }, [screens, quizByIdx]);

    function selectOption(i: number) {
        if (quiz.solved || quiz.triedWrong.includes(i)) {
            return;
        }
        setQuizByIdx((m) => {
            const st = m[idx] ?? EMPTY_QUIZ;
            return { ...m, [idx]: { ...st, selected: i } };
        });
    }

    function checkAnswer() {
        if (quiz.selected == null || quiz.solved) {
            return;
        }
        const target = screen.kind === "quiz" ? screen.answer : -1;
        if (quiz.selected === target) {
            //  Move on by itself once she is right. Long enough to read the
            //  green "Correct." and see why, short enough that tapping
            //  Continue on every question stops being the job. She can still
            //  tap it — advance() is idempotent on the last screen and the
            //  timer is cleared whenever the screen changes.
            clearAutoAdvance();
            autoAdvanceRef.current = setTimeout(() => {
                autoAdvanceRef.current = null;
                setIdx((cur) => (cur < total - 1 ? cur + 1 : cur));
            }, CORRECT_ANSWER_DWELL_MS);
        }
        setQuizByIdx((m) => {
            const st = m[idx] ?? EMPTY_QUIZ;
            if (st.selected === target) {
                return { ...m, [idx]: { ...st, solved: true } };
            }
            // Wrong: record the miss, clear the selection so she picks again.
            const triedWrong =
                st.selected == null ? st.triedWrong : [...st.triedWrong, st.selected];
            return { ...m, [idx]: { ...st, selected: null, triedWrong } };
        });
    }

    function advance() {
        //  She got there first — drop the pending auto-advance so we do not
        //  skip the screen she just landed on.
        clearAutoAdvance();
        if (idx < total - 1) {
            setIdx(idx + 1);
        }
    }

    function back() {
        //  Going back must cancel a queued advance, or it yanks her forward
        //  again a moment after she asked to go back.
        clearAutoAdvance();
        if (idx > 0) {
            setIdx(idx - 1);
        }
    }

    return {
        idx,
        screen,
        total,
        progressPct: progressPct(idx, total),
        timeLeftLabel: timeLeftLabel(idx, times),
        stepLabel: `Step ${idx + 1} of ${total}`,
        coursePill: coursePill(idx, total, course.label ?? course.title),
        quizIndex: quizPositions.get(idx) ?? 1,
        quizCount: quizPositions.size,
        canBack: idx > 0,
        isLastScreen: idx === total - 1,
        quiz,
        score,
        selectOption,
        checkAnswer,
        advance,
        back
    };
}
